import { action } from '@ember/object';
import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import * as Sentry from '@sentry/ember';
import dragula from 'dragula';
import 'dragula/dist/dragula.css';
import { DateTime } from 'scaleflow/utils/date-time';
import { scheduleTask } from 'ember-lifeline';
import { nextPositionAfter } from 'scaleflow/utils/position';

export default class OrganismsApproachPlanningComponent extends Component {
  @service store;
  @tracked useSwap = false;
  @service('organisation') orgService;
  @service('beta-toggle') betaService;

  @tracked groupBy = 'workstream';
  @tracked isCreatingWeek = false;

  datepickerInstance = null;

  visibleBadges = ['teams', 'workstreams', 'weeks'];

  draggableWeeks = null;

  get initiative() {
    return this.store.peekRecord('initiative', this.args.initiativeId);
  }

  get sortedWeeks() {
    const weeks = this.weeks;
    const sortedWeeks = [...weeks.slice()].sort((a, b) => {
      if (a.position > b.position) return 1;
      if (a.position < b.position) return -1;
      return 0;
    });

    return sortedWeeks;
  }

  get weeks() {
    return this.initiative.get('weeks')?.content ?? [];
  }

  get sortOptions() {
    return [
      {
        label: 'Group by Workstream',
        value: 'workstream',
      },
      {
        label: 'Group by Assignee',
        value: 'assignee',
      },
    ];
  }

  @action
  initWeekOpenState() {
    const currentWeek = this.sortedWeeks.find((week) => week.isCurrentWeek);
    if (currentWeek) {
      scheduleTask(this, 'actions', () => {
        currentWeek.weekOpen = true;
      });
    }
  }

  // A week is in `initiative.weeks` from the moment it is created, but has no
  // id until its POST lands — and `WeekModel.save()` also re-saves the
  // initiative to keep its duration in step, so that window is two round trips
  // wide. A second click inside it used to read the half-created week as the
  // last one and crash looking up a null id.
  @action
  async createWeek() {
    if (this.isCreatingWeek) return;
    this.isCreatingWeek = true;

    let week;
    try {
      let initiative = this.store.peekRecord(
        'initiative',
        this.args.initiativeId,
      );
      let position = await this.lastWeekPosition();
      week = this.store.createRecord('week', {
        title: 'The one where...',
        text: '',
        position,
        initiative,
      });
      await week.save();
    } catch (error) {
      // Without this the failed record keeps sitting in `initiative.weeks` with
      // a null id, so every later click hits the same crash.
      week?.rollbackAttributes();
      Sentry.captureException(error);
    } finally {
      this.isCreatingWeek = false;
    }
  }

  async lastWeekPosition() {
    return nextPositionAfter(await this.weeks);
  }

  @action
  onDrop(el, target) {
    // A week still being created renders without a `data-week-id`, so it has no
    // record to renumber. Counting separately from the DOM index keeps the
    // positions it does write contiguous — `isCurrentWeek` reads `position` as
    // an offset in weeks from the initiative's start date, so a gap would skip
    // a calendar week.
    let position = 1;
    for (const child of target.children) {
      const id = child.dataset.weekId;
      const week = id ? this.store.peekRecord('week', id) : null;
      if (!week) continue;
      week.position = position;
      week.save();
      position += 1;
    }
  }

  @action
  onDrag() {}

  @action
  onDraggableContainerRender(e) {
    this.draggableWeeks = dragula({
      containers: [document.getElementById(e.id)],
      revertOnSpill: true,
      direction: 'vertical',
      moves: function (_, source, handle) {
        return handle?.classList?.contains('sort-icon') ?? false;
      },
    });

    this.draggableWeeks.on('drop', this.onDrop);
    this.draggableWeeks.on('drag', this.onDrag);
  }

  get defaultDate() {
    const start_date = this.initiative?.startDate;
    if (!start_date) return null;
    else {
      const date = DateTime.from(start_date);
      return date.isValid() ? date.toDate() : null;
    }
  }

  @action
  onDateChange(e) {
    const date = e[0];
    const yyyy_mm_dd = DateTime.from(date).format('YYYY-MM-DD');
    const initiative = this.initiative;
    initiative.startDate = yyyy_mm_dd;
    initiative.save();
  }

  @action
  onReady(_, __, instance) {
    this.datepickerInstance = instance;
  }

  @action
  toggleDatepicker() {
    this.datepickerInstance.toggle();
  }

  @action
  handleSortSelect(e) {
    this.groupBy = e.value;
  }
}
